import React from "react";
import {
  Box,
  Card,
  Grid,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
  Skeleton,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { FiFileText, FiBookOpen, FiDownload } from "react-icons/fi";
import BadgeImage from "../../../../components/badges_image/badgesImage";
import { PiIdentificationCard } from "react-icons/pi";
import { FaCalendarAlt, FaDesktop, FaEnvelope, FaMedal } from "react-icons/fa";
import { useSelector } from "react-redux";
import { downloadBadge } from "../../../../reward_sys/badgeDownload/index";

const BadgeDetails = ({ isUserDetails }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { userDetails, badgeDetails, loading } = useSelector(
    (state) => state.rewardDashboard
  );

  const cardStyle = {
    background: "transparent",
    borderRadius: "10px",
    p: 2,
    height: "100%",
  };

  const minPoints = badgeDetails?.min_point || 0;
  const maxPoints = badgeDetails?.max_point || 2;
  const currentPoints = badgeDetails?.points || 0;

  const progress = Math.max(
    0,
    Math.min(100, ((currentPoints - minPoints) / (maxPoints - minPoints)) * 100)
  );

  return (
    <Box sx={{ borderRadius: "12px" }}>
      <Grid
        container
        spacing={2}
        direction={isMobile ? "column" : "row"}
        justifyContent="space-between"
      >
        {/* Profile Section */}
        {isUserDetails && (
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                background: "var(--org-bg-tint-2, #fff7f6)",
                borderRadius: "16px",
                boxShadow: 1,
                p: 3,
                height: "100%",
              }}
            >
              {/* Top Section */}
              <Box display="flex" alignItems="center" mb={2}>
                {loading ? (
                  <Skeleton variant="circular" width={64} height={64} />
                ) : (
                  <Avatar
                    src={userDetails.profile_image || ""}
                    alt="Profile"
                    sx={{ width: 64, height: 64, mr: 2 }}
                  />
                )}
                <Box ml={2}>
                  {loading ? (
                    <Skeleton width={120} height={20} />
                  ) : (
                    <Typography fontWeight={700} fontSize={18}>
                      {userDetails.first_name} {userDetails.last_name}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Typography
                fontSize={14}
                fontWeight={600}
                sx={{
                  borderBottom: "2px solid var(--org-bg-color, #f6615f)",
                  display: "inline-block",
                  mb: 2,
                  color: "var(--org-bg-color, #f6615f)",
                }}
              >
                Information
              </Typography>

              <Grid container spacing={2}>
                {[
                  {
                    icon: <FaCalendarAlt size={16} />,
                    label: "Date Applied",
                    value: userDetails.selected_date,
                  },
                  {
                    icon: <FaDesktop size={16} />,
                    label: "Position",
                    value: userDetails.position || "Not Provided",
                  },
                  {
                    icon: <PiIdentificationCard size={16} />,
                    label: "Employee ID",
                    value: userDetails.employee_id || "Not Provided",
                  },
                  {
                    icon: <FaEnvelope size={16} />,
                    label: "Email",
                    value: userDetails.email,
                  },
                ].map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {item.icon}
                      <Box>
                        <Typography fontSize={12} color="text.secondary">
                          {item.label}
                        </Typography>
                        {loading ? (
                          <Skeleton width={120} height={20} />
                        ) : (
                          <Typography fontSize={14} fontWeight={600}>
                            {item.value}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Card>
          </Grid>
        )}

        {/* Learning Overview */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: 2,
              background: "var(--org-bg-tint-2, #fff7f6)",
              borderRadius: "12px",
              boxShadow: theme.shadows[1],
              height: "100%",
            }}
          >
            <Typography fontWeight={700} mb={2}>
              Learning Overview
            </Typography>

            <Grid container spacing={2}>
              {/* Cards Read */}
              <Grid item xs={12} sm={6}>
                <Box sx={cardStyle}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <FiFileText size={18} />
                    <Typography fontSize={14} fontWeight={600}>
                      Cards Read
                    </Typography>
                  </Box>
                  {loading ? (
                    <>
                      <Skeleton width={60} height={24} />
                      <Skeleton width={100} height={16} />
                    </>
                  ) : (
                    <>
                      <Typography fontSize={18} fontWeight={700} color="error">
                        {badgeDetails.readings_cards} Cards
                      </Typography>
                      <Typography fontSize={12} color="error">
                        {badgeDetails.total_readings} Cards /{" "}
                        {badgeDetails.total_cards} Cards
                      </Typography>
                    </>
                  )}
                </Box>
              </Grid>

              {/* Micro Trainings */}
              <Grid item xs={12} sm={6}>
                <Box sx={cardStyle}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <FiBookOpen size={18} />
                    <Typography fontSize={14} fontWeight={600}>
                      Micro Trainings
                    </Typography>
                  </Box>
                  {loading ? (
                    <Skeleton width={40} height={24} />
                  ) : (
                    <Typography fontSize={18} fontWeight={700} color="error">
                      {badgeDetails.trainings || 0}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Badges Earned */}
              <Grid item xs={12}>
                <Box sx={cardStyle}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <FaMedal size={18} />
                    <Typography fontSize={14} fontWeight={600}>
                      Badges Earned
                    </Typography>
                  </Box>
                  {/* Row Layout */}
                  <Box
                    display="flex"
                    gap={3}
                    alignItems="center"
                    justifyContent="center"
                    flexWrap="wrap"
                    sx={{ overflowX: "auto", pb: 1 }}
                  >
                    {loading
                      ? [...Array(5)].map((_, i) => (
                          <Skeleton
                            key={i}
                            variant="circular"
                            width={60}
                            height={60}
                          />
                        ))
                      : badgeDetails.medals?.map((badge, idx) => (
                          <Box
                            key={idx}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            sx={{ minWidth: "80px", textAlign: "center" }}
                          >
                            {/* Badge Image */}
                            <BadgeImage
                              badgeName={(
                                badge.badgeName || "default"
                              ).toLowerCase()}
                              altText={badge.badgeName || "Badge"}
                              width={60}
                              height={60}
                            />

                            {/* Count */}
                            <Typography fontSize={12} fontWeight={600} mt={0.5}>
                              {badge.count}
                            </Typography>

                            {/* Action Section */}
                            {badge.count === 0 ? (
                              <Typography
                                fontSize={12}
                                color="text.secondary"
                                sx={{
                                  mt: 0.5,
                                  px: 1,
                                  py: 0.2,
                                  borderRadius: "6px",
                                  background: "#f5f5f5",
                                }}
                              >
                                Keep going!
                              </Typography>
                            ) : badge.count === 1 ? (
                              <button
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  fontSize: "12px",
                                  borderRadius: "6px",
                                  border: "1px solid #ccc",
                                  background: "#fff",
                                  cursor: "pointer",
                                  marginTop: "6px",
                                }}
                                onClick={() =>
                                  downloadBadge(
                                    badge.badgeName,
                                    `${userDetails.first_name} ${userDetails.last_name}`,
                                    badge.individualBadges[0] || {}
                                  )
                                }
                              >
                                Download <FiDownload size={14} />
                              </button>
                            ) : (
                              <FormControl
                                size="small"
                                sx={{
                                  mt: 1,
                                  minWidth: 80,
                                  "& .MuiOutlinedInput-root": {
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                    borderRadius: "6px",
                                    background: "#fff",
                                    padding: 0,
                                    height: "22px",
                                  },
                                  "& .css-jedpe8-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input.css-jedpe8-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input.css-jedpe8-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input":
                                    {
                                      padding: 0,
                                    },
                                }}
                              >
                                <Select
                                  displayEmpty
                                  value=""
                                  IconComponent={() => null}
                                  renderValue={() => (
                                    <Box
                                      gap={0.5}
                                      alignItems="center"
                                      display={"flex"}
                                      padding={0.8}
                                    >
                                      <Typography
                                        fontSize={12}
                                        fontWeight="bold"
                                        color="black"
                                      >
                                        Download
                                      </Typography>
                                      <FiDownload size={14} />
                                    </Box>
                                  )}
                                  onChange={() => {}}
                                >
                                  {badge.individualBadges?.map((ind, i) => (
                                    <MenuItem
                                      key={i}
                                      value={ind.title}
                                      onClick={() => {
                                        downloadBadge(
                                          badge.badgeName,
                                          `${userDetails.first_name} ${userDetails.last_name}`,
                                          ind
                                        );
                                      }}
                                      style={{ borderBottom: "1px solid #ccc" }}
                                    >
                                      <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        width="100%"
                                        gap={1}
                                      >
                                        <Typography fontSize={12} color="black">
                                          {ind.date}
                                        </Typography>
                                        <FiDownload size={14} />
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                          </Box>
                        ))}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Progress Section */}
        {!isUserDetails && (
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                p: 2,
                background: "var(--org-bg-tint-2, #fff7f6)",
                borderRadius: "12px",
                boxShadow: theme.shadows[1],
                height: "100%",
              }}
            >
              <Typography fontWeight={700}>Your Progress</Typography>
              <Box sx={{ textAlign: "center", justifyItems: "center" }}>
                <Typography fontSize={14} color="error" fontWeight={600}>
                  Current Badge
                </Typography>

                {loading ? (
                  <Skeleton variant="circular" width={210} height={210} />
                ) : (
                  <BadgeImage
                    badgeName={
                      badgeDetails.badgeName === "No Badge"
                        ? "default"
                        : badgeDetails.badgeName?.toLowerCase()
                    }
                    width={210}
                    height={210}
                    altText={badgeDetails.badgeName}
                  />
                )}

                {loading ? (
                  <Skeleton
                    variant="rectangular"
                    width="80%"
                    height={10}
                    sx={{ my: 1, mx: "auto", borderRadius: "5px" }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 10,
                      width: "80%",
                      background: "#ddd",
                      borderRadius: "5px",
                      overflow: "hidden",
                      my: 1,
                      mx: "auto",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${
                          badgeDetails?.max_point === ""
                            ? "100%"
                            : Math.round(progress)
                        }%`,
                        background: "var(--org-bg-color, #f6615f)",
                        transition: "width 0.3s ease-in-out",
                      }}
                    />
                  </Box>
                )}

                {loading ? (
                  <Skeleton width={80} height={20} />
                ) : (
                  <Typography fontSize={14} fontWeight={600}>
                    {currentPoints} Points
                  </Typography>
                )}
              </Box>

              <Box display="flex" justifyContent="space-between" mb={1}>
                {loading ? (
                  <>
                    <Skeleton width="60%" height={20} />
                    <Skeleton width={40} height={20} />
                  </>
                ) : (
                  <>
                    {badgeDetails?.max_point !== "" ? (
                      <Typography fontSize={14} color="rgba(52, 53, 60, 1)">
                        Minimum {Math.max(0, maxPoints - currentPoints)+1} Points
                        Need to Next Level
                      </Typography>
                    ) : (
                      <Typography fontSize={14} color="rgba(52, 53, 60, 1)">
                        Special Level – You have earned maximum points
                      </Typography>
                    )}
                    <Typography fontSize={12} fontWeight={600}>
                      {`${
                        badgeDetails?.max_point === "" ? "100" : Math.round(progress)
                      }%`}
                    </Typography>
                  </>
                )}
              </Box>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default BadgeDetails;
